// Find-a-player search over a rendered Rankings leaderboard, ported from
// renderers-rankings.js (renderRankingSearchResults + the jump-by-name re-locate).
// Pure so it's unit-tested; the React search UI in RankingsModule drives it.
//
// Two rules from the legacy, kept exactly:
//  - Search is scoped to THIS rendered leaderboard (the `entries` array), never the
//    whole database — a player filtered out or with no value here honestly has no row.
//  - A jump is remembered BY NAME, not index: switching metric/season/mode/filter
//    reorders the list, so the searched row is re-located by who it is (or drops out).

import { LIVE } from "../live/flag.js";
import { fuzzyFilter } from "../data/fuzzy-search.js";
import type { ProtoRankingEntry } from "@catbento/shared";

export interface RankingSearchMatch {
  entry: ProtoRankingEntry;
  /** Position in the current leaderboard (0-based); the row's rank is index + 1. */
  index: number;
}

/**
 * The top matches for a search query, by case-insensitive shortName substring, in
 * leaderboard order, capped at `limit` (renderers-rankings.js:485). An empty or
 * whitespace-only query returns [] (the dropdown stays closed).
 */
export function matchRankingSearch(
  entries: readonly ProtoRankingEntry[],
  query: string,
  limit = 8,
): RankingSearchMatch[] {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  if (LIVE) {
    // Live: forgiving (accents, word order, one typo), best first (Ben, 2026-10-16).
    const indexed = entries.map((entry, index) => ({ entry, index }));
    return fuzzyFilter(indexed, query, (m) => `${m.entry.shortName} ${m.entry.name}`).slice(0, limit);
  }
  const matches: RankingSearchMatch[] = [];
  for (let index = 0; index < entries.length; index++) {
    const entry = entries[index];
    if (entry && entry.shortName.toLowerCase().includes(q)) {
      matches.push({ entry, index });
      if (matches.length >= limit) break;
    }
  }
  return matches;
}

/**
 * Where a previously-jumped player sits in the current leaderboard, or -1 if they no
 * longer have a row (filtered out / no value this mode+season) — renderers-rankings.js:571.
 */
export function findJumpIndexByName(
  entries: readonly ProtoRankingEntry[],
  name: string | null,
): number {
  if (!name) return -1;
  return entries.findIndex((e) => e.name === name);
}
